import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Input, Select, Card, Checkbox } from "antd";
import {
  ArrowLeftOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import toast from "react-hot-toast";
import {
  updateExistingBlog,
  createNewBlog,
  fetchBlogById,
} from "../../../../redux/slice/blog/blogThunk";
import Layout from "../../../../layout/Layout";
import { useDispatch, useSelector } from "react-redux";
import { UploadFile, deleteFile } from "../../../../services/uploadFiles";
import { baseUrl } from "../../../../services/api";
import "./style.css";
import TextArea from "antd/es/input/TextArea";
const { Option } = Select;

const BlogForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const dispatch = useDispatch();
  const { selectedBlog, loading } = useSelector((state) => state.blog);

  const [formData, setFormData] = useState({
    title: "",
    content: "",
    tags: "",
    status: "draft",
    seoTitle: "",
    seoDescription: "",
    seoTags: "",
    isFeatured: false,
  });

  const [imageUrl, setImageUrl] = useState("");
  const [uploadLoading, setUploadLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);


  useEffect(() => {
    if (isEditing) {
      dispatch(fetchBlogById(id));
    }
  }, [dispatch, id, isEditing]);

  useEffect(() => {
    if (isEditing && selectedBlog) {
      setFormData({
        title: selectedBlog.title || "",
        content: selectedBlog.content || "",
        tags: selectedBlog.tags?.join(", ") || "",
        status: selectedBlog.status || "draft",
        seoTitle: selectedBlog.seoTitle || "",
        seoDescription: selectedBlog.seoDescription || "",
        seoTags: selectedBlog.seoTags?.join(", ") || "",
        isFeatured: selectedBlog.isFeatured || false,
      });
      if (selectedBlog.image) {
        setImageUrl(selectedBlog.image);
      }
    }
  }, [isEditing, selectedBlog]);

  const handleSubmit = async () => {
    if (
      !formData.title?.trim() ||
      !formData.content?.trim() ||
      !formData.tags?.trim() ||
      !formData.seoTitle?.trim() ||
      !formData.seoDescription?.trim() ||
      !formData.seoTags?.trim() ||
      !imageUrl
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (formData.isFeatured && formData.status !== "published") {   
      toast.error('To feature a blog, status must be "Published"');
      return;
    }

    const blogData = {
      title: formData.title,
      content: formData.content,
      tags: formData.tags
        .split(",")
        .map((tag) => tag.trim())
        .filter((tag) => tag),
      status: formData.status,
      author: "Admin",
      image: imageUrl || undefined,
      seoTitle: formData.seoTitle,
      seoDescription: formData.seoDescription,
      seoTags: formData.seoTags
        .split(",")
        .map((tag) => tag.trim())
        .filter((tag) => tag),
      isFeatured: formData.isFeatured,
    };

    try {
      if (isEditing) {
        await dispatch(updateExistingBlog({ blogId: id, blogData })).unwrap();
        toast.success("Blog updated successfully");
      } else {
        await dispatch(createNewBlog(blogData)).unwrap();
        toast.success("Blog created successfully");
      }
      navigate("/blog-details");
    } catch (error) {
      toast.error(error?.message || "An error occurred while saving the blog");
    }
  };

  const onUploadPicture = async (e) => {
    try {
      setUploadLoading(true);
      const promises = [...e.target.files].map(async (file) => {
        let formData = new FormData();
        formData.append("file", file);
        const { status, data: res } = await UploadFile(formData);
        if (status === 201) return res.data?.url;
        else throw new Error(res);
      });

      const results = await Promise.all(promises);

      // If old image exists, delete it
      if (imageUrl) {
        await deleteFile(imageUrl);
      }

      const newImageUrl = results[0];
      setImageUrl(newImageUrl);
      toast.success("Image uploaded successfully");
    } catch (error) {
      toast.error("Image upload failed");
    } finally {
      setUploadLoading(false);
    }
  };

  const handleRemoveImage = async () => {
    setDeleteLoading(true);
    if (!imageUrl) return;
    try {
      await deleteFile(imageUrl);
      setImageUrl("");
      toast.success("Image deleted");
    } catch (error) {
      toast.error("Failed to delete image");
    } finally {
      setDeleteLoading(false);
    }
  };

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ["bold", "italic", "underline", "strike", "blockquote", "code-block"],
      [{ color: [] }, { background: [] }],
      [{ font: [] }],
      [{ align: [] }],
      [{ script: "sub" }, { script: "super" }],
      [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
      ["link", "image", "video", "formula"],
      ["clean"],
    ],
  };

  return (
    <Layout>
      <div className="container py-4">
        <div className="d-flex align-items-center justify-between mb-4">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/blog-details")}
            className="btn btn_contained_black fs_12 btn-sm text-white px-3"
          >
            Back
          </Button>
          <div className="ml-3">
            <h2 className="text-2xl font-bold text-gray-900">
              {isEditing ? "Edit Blog" : "Create New Blog"}
            </h2>
            <p className="text-gray-600">
              {isEditing
                ? "Update your blog post"
                : "Write and publish a new blog post"}
            </p>
          </div>
        </div>

        <Card className="shadow-sm">
          {/* Title */}
          <div className="mb-4">
            <h5>
              Blog Title <span className="text-danger">*</span> 
            </h5>
            <Input
              size="large"
              placeholder="Enter blog title..."
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
            />
          </div>

          {/* Tags & Status */}
          <div className="row mb-4">
            <div className="col-md-6 mb-3 mb-md-0">
              <h5>
                Tags <span className="text-danger">*</span>
              </h5>
              <Select
                size="large"
                placeholder="Enter tags..."
                mode="tags"
                value={
                  formData.tags
                    ? formData.tags
                        .split(",")
                        .map((tag) => tag.trim())
                        .filter((tag) => tag)
                    : []
                }
                onChange={(tags) => {
                  if (tags.length > 5) {
                    toast.error("You can add up to 5 tags only");
                    return;
                  }
                  setFormData({ ...formData, tags: tags.join(", ") });
                }}
                className="w-100"
                tokenSeparators={[","]}
                showSearch={false}
                showArrow={false}
                open={false}
                maxTagCount={5}
              />
              <div className="form-text">
                Separate multiple tags with commas (e.g., React, JavaScript).
                Max 5 tags.
              </div>
            </div>

            <div className="col-md-6">
              <label className="form-label">Status</label>
              <Select
                size="large"
                value={formData.status}
                onChange={(value) =>
                  setFormData({ ...formData, status: value })
                }
                className="w-100"
              >
                <Option value="draft">Draft</Option>
                <Option value="published">Published</Option>
              </Select>
            </div>
          </div>

          <div className="mb-4">
            <Checkbox
              checked={formData.isFeatured}
              onChange={e =>
                setFormData({ ...formData, isFeatured: e.target.checked })
              }
            >
              Featured
            </Checkbox>
          </div>

          {/* SEO Tags & Status */}
          <div className="mb-4">
            <h5>
              SEO Blog Title <span className="text-danger">*</span> 
            </h5>
            <Input
              size="large"
              placeholder="Enter blog title..."
              value={formData.seoTitle}
              onChange={(e) =>
                setFormData({ ...formData, seoTitle: e.target.value })
              }
            />
          </div>

          <div className="mb-4">
            <h5>
              SEO Blog Description <span className="text-danger">*</span>
            </h5>
            <TextArea
              size="large"
              placeholder="Enter blog description..."
              value={formData.seoDescription}
              onChange={(e) =>
                setFormData({ ...formData, seoDescription: e.target.value })
              }
              rows={4} 
            />
          </div>

           <div className="mb-3 mb-md-0 mb-4">
              <h5>
                SEO Tags <span className="text-danger">*</span>
              </h5>
              <Select
                size="large"
                placeholder="Enter tags..."
                mode="tags"
                value={
                  formData.seoTags
                    ? formData.seoTags
                        .split(",")
                        .map((tag) => tag.trim())
                        .filter((tag) => tag)
                    : []
                }
                onChange={(tags) => {
                  if (tags.length > 5) {
                    toast.error("You can add up to 5 tags only");
                    return;
                  }
                  setFormData({ ...formData, seoTags: tags.join(", ") });
                }}
                className="w-100"
                tokenSeparators={[","]}
                showSearch={false}
                showArrow={false}
                open={false}
                maxTagCount={5}
              />
              <div className="form-text">
                Separate multiple tags with commas (e.g., React, JavaScript).
                Max 5 tags.
              </div>
            </div>

          {/* Image Upload */}
          <div className="mb-4">
            <h5>
              Featured Image <span className="text-danger">*</span>
            </h5>
            <div className="d-flex align-items-center gap-3">
              <input
                type="file"
                accept="image/*"
                onChange={onUploadPicture}
                hidden
                id="blog-upload-image"
              />
              <label
                htmlFor="blog-upload-image"
                className="btn btn-sm btn_contained_black fs_12 btn-sm text-white px-3"
                style={{ cursor: "pointer" }}
              >
                {uploadLoading ? "Uploading..." : "Upload Image"}
              </label>
            </div>
            {/* remove image button */}


            {imageUrl && (
              <div className="d-flex align-items-center gap-3 mt-2">
                <Button
                  danger
                  size="small"
                  onClick={handleRemoveImage}
                  loading={deleteLoading}
                  className="btn btn-sm btn-danger fs_12 text-white px-3"
                >
                  {deleteLoading ? "Deleting..." : "Remove Image"}
                </Button>
              </div>
            )}
            {/* remove image button */}



            {imageUrl && (
              <div className="mt-3">
                <img
                  src={`${baseUrl}${imageUrl}`}
                  alt="Preview"
                  className="img-fluid rounded shadow-sm"
                  style={{ maxHeight: "200px", objectFit: "cover" }}
                />
              </div>
            )}
          </div>

          {/* Content Editor */}
          <div className="mb-4">
            <h5>
              Content <span className="text-danger">*</span>
            </h5>
            <div className="border rounded">
              <ReactQuill
                theme="snow"
                value={formData.content}
                onChange={(content) =>
                  setFormData({ ...formData, content })
                }
                modules={quillModules}
                // style={{ minHeight: "300px" }}
              />
            </div>
          </div>

          {/* Footer Buttons */}
          <div className="d-flex justify-content-end gap-2 pt-3 border-top">
            <Button
              size="large"
              onClick={() => navigate("/blog-details")}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              size="large"
              icon={<SaveOutlined />}
              onClick={handleSubmit}
              loading={loading}
              className="btn btn_contained_black fs_12 btn-sm text-white px-3"
            >
              {isEditing ? "Update Blog" : "Create Blog"}
            </Button>
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default BlogForm;
